import { addSubdomain } from '@/lib/utils/url'
import {
  Attachment,
  BillingAccountCnameSettingFragment,
  Branding,
  Cname,
  FeatureTrialInfo,
  GetFormQuery,
  PublishedUrlDomainEnum,
  WeixinAccount,
} from '@/typings/domain'
import { Action, createEntityAdapter, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { merge } from 'lodash-es'
import { RootState } from '../store'
import getBillingAccountPlanInfo from '../utils/getBillingAccountPlanInfo'

const isFormFetched = (action: Action) => {
  return ['forms/fetchOne/fulfilled'].includes(action.type)
}

export type FormBillingAccountAbilities = NonNullable<GetFormQuery['form']>['billingAccountAbilities']
export type FormBillingAccountFeatureQuotas = NonNullable<GetFormQuery['form']>['billingAccountFeatureQuotas']

export type FormBillingAccount = {
  id: string
  planPackage?: Maybe<{
    plan: {
      code: string
    }
  }>
  currentFeatureTrialInfo?: Maybe<Pick<FeatureTrialInfo, 'status' | 'trialType' | 'endDate' | 'onetimeCount'>>
  branding?: Maybe<
    Pick<
      Branding,
      | 'customFooter'
      | 'customFooterEnabled'
      | 'customFooterLogoEnabled'
      | 'showQrcodeLogo'
      | 'navDisplay'
      | 'customFooterLink'
    > & {
      qrcodeLogo?: Maybe<Pick<Attachment, 'id' | 'visitUrl'>>
    } & { navLogo?: Maybe<Pick<Attachment, 'visitUrl'>> }
  >
  activeSubdomain?: Maybe<string>
  weixinAccounts?: Pick<WeixinAccount, 'id' | 'nickName' | 'verified'>[]
  cnameSetting?: BillingAccountCnameSettingFragment
  abilities?: FormBillingAccountAbilities
  featureQuotas?: FormBillingAccountFeatureQuotas
} & ReturnType<typeof getBillingAccountPlanInfo>

const billingAccountAdapter = createEntityAdapter<FormBillingAccount>()

const formBillingAccountSlice = createSlice({
  name: 'formBillingAccounts',
  initialState: billingAccountAdapter.getInitialState(),
  reducers: {},
  extraReducers: builder => {
    builder.addMatcher<PayloadAction<NonNullable<GetFormQuery['form']>>>(isFormFetched, (state, action) => {
      const { billingAccount, billingAccountAbilities, billingAccountFeatureQuotas } = action.payload
      const originBillingAccount = state.entities[billingAccount.id]
      billingAccountAdapter.upsertOne(
        state,
        merge({}, originBillingAccount, billingAccount, {
          abilities: billingAccountAbilities,
          featureQuotas: billingAccountFeatureQuotas,
          ...getBillingAccountPlanInfo(billingAccount),
        })
      )
    })
  },
})

const billingAccountSelectors = billingAccountAdapter.getSelectors()

export const selectFormBillingAccount = createSelector(
  (state: RootState) => state.formBillingAccounts,
  (state: RootState) => state.forms.entities,
  (_state: RootState, formToken?: string | null) => formToken,
  (billingAccountsState, formsEntities, formToken) => {
    const formBillingAccountId = formToken ? formsEntities[formToken]?.billingAccountId : null
    return formBillingAccountId ? billingAccountSelectors.selectById(billingAccountsState, formBillingAccountId) : null
  }
)

export const selectPublishedFormHostUrl = createSelector(
  selectFormBillingAccount,
  (state: RootState, formToken: string) => state.userFormSettings.entities[formToken],
  (billingAccount, userFormSetting) => {
    const cnameSetting = billingAccount?.cnameSetting as Pick<Cname, 'cname' | 'cnameEnabled'>
    const subdomain = billingAccount?.activeSubdomain
    const preferredDomainType = userFormSetting?.publishedUrlDomain

    const { cname, cnameEnabled = false } = cnameSetting ?? {}
    const isCustomDomain = preferredDomainType !== PublishedUrlDomainEnum.Gd && cnameEnabled && !!cname

    return isCustomDomain
      ? `https://${cname}`
      : addSubdomain(`${process.env.EXPO_PUBLIC_PUBLISHED_DOMAIN}`, subdomain).replace(/\/$/, '')
  }
)

export default formBillingAccountSlice.reducer
